import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { v4 } from 'uuid';

import {
  AlfredFooter,
  ALFRED_FOOTER_SPACER_HEIGHT_PX,
} from '~/components/alfred-footer/AlfredFooter';
import { useContentQuestions } from '~/react-query-hooks/documents/useContentQuestions';
import { TrackingMetadata } from '~/services/tracking/posthog/constants';
import { useAlfredFooterStore } from '~/stores/use-alfred-footer/useAlfredFooter';
import { useChatInputStore } from '~/stores/use-chat-input/useChatInput';
import { Watchlist } from '~/stores/use-watchlist/watchlist-types';

import { ActiveFilterOptions } from '../filters/Filters';
import styles from './WithFooterWrapper.module.scss';

export interface WithFooterWrapperProps {
  children?: React.ReactNode;
  testId?: string;
  isWrappedComponentOpened?: boolean;
  className?: string;
  emptyState?: boolean;
  questionContext?: ActiveFilterOptions[] | string | string[] | Watchlist;
  shouldHide?: boolean;
  enableAutoRun?: boolean;
  showReloadButton?: boolean;
  trackingMetadata?: TrackingMetadata;
  fixedFooter?: boolean;
}

export const WithFooterWrapper = ({
  children,
  testId,
  isWrappedComponentOpened = true,
  className,
  emptyState = false,
  questionContext,
  shouldHide = false,
  enableAutoRun = false,
  showReloadButton = false,
  trackingMetadata,
  fixedFooter = false,
}: WithFooterWrapperProps) => {
  const contentRef = useRef<HTMLElement | null>(null);
  const [questions, setQuestions] = useState<string[]>([]);
  const [queryCounter, setQueryCounter] = useState(0);
  const {
    hasUserInteracted,
    askedQuestions,
    setHasUserInteracted,
    addNewAskedQuestion,
  } = useChatInputStore(state => ({
    hasUserInteracted: state.hasUserInteracted,
    askedQuestions: state.askedQuestions,
    setHasUserInteracted: state.setHasUserInteracted,
    addNewAskedQuestion: state.addNewAskedQuestion,
  }));

  const idRef = useRef<string>('');

  if (!idRef.current) {
    idRef.current = v4();
  }

  const { activeId, push, remove } = useAlfredFooterStore(state => ({
    activeId: state.activeId,
    push: state.push,
    remove: state.remove,
  }));

  useEffect(() => {
    if (isWrappedComponentOpened) {
      push(idRef.current);
      return () => remove(idRef.current);
    }

    return undefined;
  }, [isWrappedComponentOpened, push, remove]);

  const resetQuestions = () => {
    setQuestions([]);
  };

  const isFooterActive = activeId === idRef.current;

  useEffect(() => {
    resetQuestions();
  }, [questionContext]);

  const { data, isLoading } = useContentQuestions({
    queryCounter,
    askedQuestionsLength: askedQuestions.length,
    enabled:
      !emptyState &&
      !hasUserInteracted &&
      isWrappedComponentOpened &&
      isFooterActive &&
      enableAutoRun,
  });

  useEffect(() => {
    if (hasUserInteracted) {
      setQuestions([]);
    } else {
      setQuestions(data?.questions ?? []);
    }
  }, [data]);

  const onReloadQuestion = () => {
    setHasUserInteracted(false);
    resetQuestions();
    setQueryCounter(prev => prev + 1);
  };

  const question = questions[0];

  const onSubmitFromChat = () => {
    resetQuestions();
    onReloadQuestion();
    addNewAskedQuestion(question);
  };

  const hasFooter = isWrappedComponentOpened && fixedFooter;

  return (
    <div className={styles.root} data-testid={testId}>
      <span
        ref={contentRef}
        className={clsx(styles.content, className)}
        style={
          hasFooter
            ? { paddingBottom: `${ALFRED_FOOTER_SPACER_HEIGHT_PX}px` }
            : undefined
        }
      >
        {children}
      </span>
      {isWrappedComponentOpened && (
        <AlfredFooter
          fixed={fixedFooter}
          suggestedQuestion={
            isFooterActive && enableAutoRun ? question : undefined
          }
          isActive={isFooterActive}
          isLoading={isLoading}
          emptyState={emptyState}
          shouldHide={shouldHide}
          onReload={onReloadQuestion}
          submitCallback={onSubmitFromChat}
          showReloadButton={showReloadButton}
          trackingMetadata={trackingMetadata}
        />
      )}
    </div>
  );
};
